// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';
import { Star, CheckCircle, Lock, ChevronRight, ShoppingBag, Truck, Zap } from 'lucide-react';

export default function LoyaltyPage() {
  const { user, customer, refreshCustomer } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers]   = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    const [{ data: t }] = await Promise.all([
      supabase.from('grocery_loyalty_tiers').select('*').order('min_points'),
    ]);
    setTiers(t || []);

    if (customer) {
      const { data: ord } = await supabase
        .from('grocery_orders')
        .select('order_number, total_cents, loyalty_points_earned, created_at, order_status')
        .eq('customer_id', customer.id)
        .order('created_at', { ascending: false })
        .limit(5);
      setOrders(ord || []);
    }

    setLoading(false);
  };

  const points     = customer?.loyalty_points || 0;
  const currentTier = customer?.grocery_loyalty_tiers;
  const nextTier   = tiers.find(t => t.min_points > points);
  const ptsToNext  = nextTier ? nextTier.min_points - points : 0;

  const tierColorMap = {
    'Bronze':   { from: '#CD7F32', to: '#A0522D' },
    'Silver':   { from: '#94A3B8', to: '#64748B' },
    'Gold':     { from: '#F59E0B', to: '#D97706' },
    'Platinum': { from: '#8B5CF6', to: '#6D28D9' },
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="page-container" style={{ paddingTop: 32, paddingBottom: 48 }}>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #14532D, #16A34A)',
        borderRadius: 24, padding: '40px 32px', color: '#fff', marginBottom: 40, textAlign: 'center',
      }}>
        <div style={{ fontSize: 52, marginBottom: 12 }}>⭐</div>
        <h1 style={{ fontSize: 32, fontWeight: 900, marginBottom: 8 }}>Loyalty Rewards</h1>
        <p style={{ fontSize: 16, color: '#BBF7D0', marginBottom: user ? 24 : 16 }}>
          Earn points with every purchase. Unlock exclusive benefits and bigger discounts.
        </p>
        {!user && (
          <Link to="/login" style={{ textDecoration: 'none' }}>
            <button className="btn btn-xl" style={{ background: '#fff', color: '#16A34A', fontWeight: 800 }}>
              Join Free & Start Earning
            </button>
          </Link>
        )}
        {user && (
          <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '12px 28px' }}>
            <div style={{ fontSize: 36, fontWeight: 900 }}>{points.toLocaleString()}</div>
            <div style={{ fontSize: 14, color: '#BBF7D0', fontWeight: 600 }}>Loyalty Points</div>
            {currentTier && (
              <div style={{ marginTop: 6, fontSize: 15, fontWeight: 700, color: '#fff' }}>
                {currentTier.tier_icon} {currentTier.tier_name} Member
              </div>
            )}
          </div>
        )}
      </div>

      {/* Progress to next tier */}
      {user && nextTier && currentTier && (
        <div className="card" style={{ padding: 24, marginBottom: 32 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#14532D', marginBottom: 16 }}>
            Your Progress to {nextTier.tier_icon} {nextTier.tier_name}
          </h3>
          <div style={{ height: 16, background: '#F0FDF4', borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
            <div style={{
              height: '100%',
              width: `${Math.min(100, (points / nextTier.min_points) * 100)}%`,
              background: 'linear-gradient(90deg, #16A34A, #22C55E)',
              borderRadius: 8, transition: 'width 0.8s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748B' }}>
            <span>{points.toLocaleString()} pts ({currentTier.tier_name})</span>
            <span style={{ fontWeight: 700, color: '#16A34A' }}>{ptsToNext.toLocaleString()} more to {nextTier.tier_name}</span>
            <span>{nextTier.min_points.toLocaleString()} pts</span>
          </div>
        </div>
      )}

      {/* Tiers */}
      <h2 style={{ fontSize: 24, fontWeight: 800, color: '#14532D', marginBottom: 20 }}>Membership Tiers</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 40 }}>
        {tiers.map(tier => {
          const colors   = tierColorMap[tier.tier_name] || { from: '#94A3B8', to: '#64748B' };
          const isActive = currentTier?.id === tier.id;
          const isLocked = user && points < tier.min_points;

          return (
            <div key={tier.id} style={{
              borderRadius: 20, overflow: 'hidden',
              border: isActive ? '3px solid #16A34A' : '2px solid #BBF7D0',
              boxShadow: isActive ? '0 8px 24px rgba(22,163,74,0.2)' : '0 2px 8px rgba(0,0,0,0.05)',
              position: 'relative',
            }}>
              {isActive && (
                <div style={{ position: 'absolute', top: 12, right: 12, background: '#16A34A', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12 }}>
                  YOUR TIER
                </div>
              )}
              {/* Tier header */}
              <div style={{ background: `linear-gradient(135deg, ${colors.from}, ${colors.to})`, padding: '24px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 6 }}>{tier.tier_icon}</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#fff' }}>{tier.tier_name}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>
                  {tier.min_points.toLocaleString()} points
                </div>
              </div>
              {/* Perks */}
              <div style={{ padding: 16, background: '#fff' }}>
                {(tier.perks || []).map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0', borderBottom: i < tier.perks.length - 1 ? '1px solid #F0FDF4' : 'none' }}>
                    <CheckCircle size={14} color="#16A34A" style={{ flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: 13, color: '#374151' }}>{p.perk}</span>
                  </div>
                ))}
              </div>
              {isLocked && (
                <div style={{ background: '#F8FAFC', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #F0FDF4' }}>
                  <Lock size={14} color="#94A3B8" />
                  <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>
                    {(tier.min_points - points).toLocaleString()} more points needed
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* How to earn */}
      <div className="card" style={{ padding: 24, marginBottom: 32 }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#14532D', marginBottom: 20 }}>How to Earn Points</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          {[
            { icon: <ShoppingBag size={24} color="#16A34A" />, title: '1 pt per $1', body: 'Earn points on every purchase (Bronze).' },
            { icon: <Zap size={24} color="#F59E0B" />,        title: '1.5x Silver',  body: 'Silver members earn 1.5 points per dollar.' },
            { icon: <Star size={24} color="#EF4444" />,       title: '2x Gold+',     body: 'Gold and Platinum earn 2x points always.' },
            { icon: <Truck size={24} color="#8B5CF6" />,      title: 'Free Delivery', body: 'Gold+ get free delivery on every order.' },
          ].map(item => (
            <div key={item.title} style={{ textAlign: 'center', padding: '16px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>{item.icon}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#14532D', marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 13, color: '#64748B' }}>{item.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent point history */}
      {user && orders.length > 0 && (
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#14532D', marginBottom: 16 }}>Recent Points History</h3>
          {orders.map(ord => (
            <div key={ord.order_number} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 0', borderBottom: '1px solid #F0FDF4',
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#14532D' }}>Order #{ord.order_number}</div>
                <div style={{ fontSize: 12, color: '#94A3B8' }}>
                  {new Date(ord.created_at).toLocaleDateString()} · {(ord.total_cents / 100).toFixed(2)} spent
                </div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#16A34A' }}>
                +{ord.loyalty_points_earned || 0} pts
              </div>
            </div>
          ))}
          <Link to="/orders" style={{ textDecoration: 'none' }}>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 12, color: '#16A34A' }}>
              View all orders <ChevronRight size={14} />
            </button>
          </Link>
        </div>
      )}

      {!user && (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <Link to="/login" style={{ textDecoration: 'none' }}>
            <button className="btn btn-primary btn-lg">
              Sign In to View Your Points
            </button>
          </Link>
        </div>
      )}
    </div>
  );
}
